// client/src/hooks/useInterestExpression.js
import { useState, useRef, useCallback, useMemo } from 'react'
import ApiInterestedUsers from '../api/ApiInterestedUsers'
import toastr from 'toastr'
import useAutofillSync from './useAutofillSync'

/**
 * Custom hook for managing interest expression form state and submission
 * Used by users who don't have an access code
 *
 * @returns {Object} Form state and handlers for interest expression
 */
export default function useInterestExpression() {
  const [showForm, setShowForm] = useState(false)
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState(null)

  // Ref for autofill detection
  const emailRef = useRef(null)

  // Sync autofilled email to state
  const handleAutofillSync = useCallback((values) => {
    if (values.email) {
      setEmail(values.email)
    }
  }, [])

  // Memoize refs mapping to avoid re-running the effect on each render
  const autofillRefs = useMemo(() => ({ email: emailRef }), [])
  useAutofillSync(autofillRefs, handleAutofillSync)

  const toggleForm = () => {
    setShowForm(!showForm)
  }

  const handleEmailChange = (e) => {
    setEmail(e.target.value)
    // Clear error when user starts typing again
    if (error) {
      setError(null)
    }
  }

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation() // Prevent event from bubbling to parent form
    }

    // Basic validation
    if (!email) {
      toastr.error('Please enter your email address')
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      toastr.error('Please enter a valid email address')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      const response = await ApiInterestedUsers.create(email)

      if (response.success) {
        setSubmitted(true)
        setEmail('')
        toastr.success(response.data?.message || 'Thank you for your interest!')
      }
    } catch (error) {
      console.error('Error submitting interest:', error)
      const errorMessage =
        error.response?.data?.error ||
        'Failed to submit. Please try again later.'
      setError(errorMessage)
      toastr.error(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  return {
    showForm,
    email,
    submitting,
    submitted,
    error,
    emailRef,
    toggleForm,
    handleEmailChange,
    handleSubmit,
  }
}
